import {
  homeRulesDefaults,
  itemGroupDefaults,
  basesHbDefaults,
  moduleLevelBedDefaults,
} from '../hb/defaults';
import {
  BUILD_CATEGORY_RESERVED,
  HOME_SKILLS,
  ITEM_MOBILITIES,
  STAT_IDS,
  STORAGE_GROUP_OTHER,
  type HomeRulesDef,
  type ItemGroupDef,
} from '../hb/types';
import { SKILL_IDS } from '../skills/types';
import { basesDefaults, withDefaults } from '../teamsDefaults';
import { num, type Err } from './common';
import { type Bundle, type BundleKey } from './types';

/*
 * The homebase redesign sections, ports of three shared-cs partials with the same messages in the same order (card 17):
 * - ContentValidator.HomeSkills.cs (card 5): infirmary beds on module levels, and the derived home-skill formulas on status effects
 *   (the perk is a perks[] row owned by the formula's stat or skill, ADR 0066).
 * - ContentValidator.Inventory.cs (card 6, ADR 0048): items[].mobility and loadoutRules.
 * - ContentValidator.Home.cs (card 7, ADR 0046/0049): homeRules, storageGroups, buildCategories and bases.autoHome*.
 * The server runs them after the Effects and Stats partials, in this order.
 */

/** Mirrors `ContentValidator.MaxBedsPerLevel`. */
export const MAX_BEDS_PER_LEVEL = 10;

export function validateHomeSkills(b: Bundle, err: Err) {
  (b.baseModules ?? []).forEach((m, i) => {
    (m?.levels ?? []).forEach((raw, j) => {
      if (raw == null) return;
      const l = { ...moduleLevelBedDefaults(), ...withDefaults({}, raw) };
      const w = `Module '${m!.id ?? ''}' level ${num(l.level)}`;
      const at = (k: string) => `levels.${j}.${k}`;
      const beds = num(l.beds);
      if (beds < 0 || beds > MAX_BEDS_PER_LEVEL)
        err('baseModules', i, at('beds'), `${w} needs beds in 0..${MAX_BEDS_PER_LEVEL}.`);
      if (beds <= 0) return;
      const mult = num(l.bedDurationMult);
      if (!(mult > 0 && mult <= 1))
        err(
          'baseModules',
          i,
          at('bedDurationMult'),
          `${w} needs bedDurationMult above 0 and at most 1.`,
        );
      if (num(l.bedHealPerDay) < 0)
        err('baseModules', i, at('bedHealPerDay'), `${w} needs bedHealPerDay of 0 or more.`);
    });
  });

  if (b.statusEffects == null) return;
  const skills = new Map<string, string>();
  b.statusEffects.forEach((e, i) => {
    if (e?.derived == null) return;
    const fail = (path: string, message: string) => err('statusEffects', i, path, message);
    const d = e.derived;
    const id = e.id ?? '';
    const skill = d.skill ?? '';
    const stat = d.stat ?? '';
    const perPoint = num(d.perPoint);
    const w = `Effect '${id}' derived`;
    if ((e.duration === null ? null : (e.duration?.kind ?? 'timed')) !== 'derived')
      fail(
        'duration',
        `Effect '${id}' has a derived formula, so it needs duration.kind 'derived'.`,
      );
    if ((e.scope ?? 'player') !== 'base')
      fail('scope', `Effect '${id}' derives a home skill, so it needs scope 'base'.`);
    const known = skill === 'build' || skill === 'beds' ? HOME_SKILLS[skill] : null;
    if (known == null) {
      fail('derived.skill', `${w} has unknown skill '${skill}' (build or beds).`);
    } else {
      for (const m of e.modifiers ?? [])
        if (m != null && m.key !== known.key)
          fail(
            'modifiers',
            `${w} skill '${skill}' writes '${known.key}', so it can't list modifier '${m.key ?? ''}'.`,
          );
    }
    // ADR 0066: the formula picks the best by a stat or (the seed) a skill: Construction for builds, Medicine for beds.
    const rows = b.skills ?? [];
    const isSkill =
      rows.length > 0 ? rows.some((x) => x != null && x.id === stat) : SKILL_IDS.includes(stat);
    const isStat = (STAT_IDS as readonly string[]).includes(stat);
    if (!isStat && !isSkill) fail('derived.stat', `${w} has unknown stat or skill '${stat}'.`);
    if (!Number.isFinite(perPoint) || Math.abs(perPoint) > 1)
      fail('derived.perPoint', `${w} needs perPoint in -1..1.`);
    if (skill === 'build' && perPoint > 0)
      fail(
        'derived.perPoint',
        `${w} build skill needs perPoint of 0 or less (a skill makes builds faster).`,
      );
    if (skill === 'beds' && perPoint < 0)
      fail(
        'derived.perPoint',
        `${w} beds skill needs perPoint of 0 or more (a skill makes beds faster).`,
      );
    const perkId = d.perkId ?? '';
    if (perkId !== '') {
      // ADR 0066: the perk is a perks[] row owned by the formula's stat or skill; its number is its modifier on the skill's key.
      const perk = (b.perks ?? []).find((p) => p != null && p.id === perkId) ?? null;
      if (perk == null) {
        fail('derived.perkId', `${w} perkId '${perkId}' is not a perks id.`);
      } else {
        const owner = `${isStat ? 'stat' : 'skill'}:${stat}`;
        if ((perk.owner ?? '') !== owner)
          fail(
            'derived.perkId',
            `${w} perk '${perkId}' is owned by ${perk.owner ?? ''}, but the formula picks by ${stat}.`,
          );
        if (known != null && !(perk.modifiers ?? []).some((m) => m != null && m.key === known.key))
          fail(
            'derived.perkId',
            `${w} perk '${perkId}' needs a '${known.key}' modifier (the skill's perk bonus).`,
          );
      }
    }
    if (known == null) return;
    const first = skills.get(skill);
    if (first !== undefined)
      fail(
        'derived.skill',
        `Effects '${first}' and '${id}' both derive the ${skill} home skill (one effect per skill: two skills never stack).`,
      );
    else skills.set(skill, id);
  });
}

const SLOT_ID = /^[a-z][a-zA-Z0-9]*$/;
const GROUP_ID = /^[a-z][a-z0-9_]*$/;

export function validateInventory(b: Bundle, err: Err) {
  (b.items ?? []).forEach((i, n) => {
    if (i == null) return;
    const mobility = i.mobility ?? '';
    if (mobility.length > 0 && !(ITEM_MOBILITIES as readonly string[]).includes(mobility))
      err(
        'items',
        n,
        'mobility',
        `Item '${i.id ?? ''}' has unknown mobility '${mobility}' (loadout, haul or empty).`,
      );
    else if (
      mobility === 'loadout' &&
      (i.category === 'key_part' || i.category === 'workshop_tool')
    )
      err(
        'items',
        n,
        'mobility',
        `Item '${i.id ?? ''}' is a ${i.category} and can't be loadout (goods).`,
      );
  });
  const l = b.loadoutRules;
  if (l == null) return;
  const fail = (path: string, message: string) => err('loadoutRules', undefined, path, message);
  if (num(l.maxGoods) < 0) fail('maxGoods', 'Loadout rules need maxGoods >= 0.');
  const seen = new Set<string>();
  (l.slots ?? []).forEach((s, j) => {
    const at = (k: string) => `slots.${j}.${k}`;
    if (s == null) {
      fail(`slots.${j}`, 'Loadout rules have an empty slot.');
      return;
    }
    const id = s.id ?? '';
    if (id === '' || !SLOT_ID.test(id))
      fail(at('id'), `Loadout slot '${id}' needs a lower camelCase id.`);
    else if (seen.has(id)) fail(at('id'), `Loadout slot '${id}' is listed twice.`);
    else seen.add(id);
    if (!s.label) fail(at('label'), `Loadout slot '${id}' needs a label.`);
    if ((s.accepts?.categories?.length ?? 0) === 0 && (s.accepts?.tags?.length ?? 0) === 0)
      fail(at('accepts'), `Loadout slot '${id}' accepts nothing (give it categories or tags).`);
  });
}

export function validateHome(b: Bundle, err: Err) {
  const items = new Set<string>();
  for (const i of b.items ?? []) if (i != null && i.id) items.add(i.id);
  homeRules(b.homeRules, items, err);
  itemGroups(b.storageGroups, 'storageGroups', 'Storage group', items, false, err);
  itemGroups(b.buildCategories, 'buildCategories', 'Build category', items, true, err);
  autoHome(b, err);
}

function homeRules(raw: HomeRulesDef | null | undefined, items: Set<string>, err: Err) {
  const fail = (path: string, message: string) => err('homeRules', undefined, path, message);
  if (raw === null) {
    fail('', 'Home rules are missing.');
    return;
  }
  const d = homeRulesDefaults();
  const h = withDefaults(d, raw ?? d);
  const w = 'Home rules';
  const notNegative = (path: string, v: number | undefined) => {
    if (num(v) < 0) fail(path, `${w} ${path} is negative.`);
  };
  const atLeast = (path: string, v: number | undefined, min: number) => {
    if (num(v) < min) fail(path, `${w} ${path} must be at least ${min}.`);
  };
  const beds = { ...d.beds, ...withDefaults({}, h.beds ?? {}) };
  const admit = num(beds.admitBelowHealthFrac);
  if (admit <= 0 || admit > 1)
    fail('beds.admitBelowHealthFrac', 'Home rules need beds.admitBelowHealthFrac in (0, 1].');
  const minDur = num(beds.minDurationMult);
  if (minDur <= 0 || minDur > 1)
    fail('beds.minDurationMult', 'Home rules need beds.minDurationMult in (0, 1].');
  notNegative('buildMarkMinSeconds', h.buildMarkMinSeconds);
  notNegative('dischargeMarkMinHours', h.dischargeMarkMinHours);
  notNegative('homeMarksPerDay', h.homeMarksPerDay);
  atLeast('onTableMax', h.onTableMax, 1);
  atLeast('logMax', h.logMax, 1);
  const headline = { ...d.headline, ...withDefaults({}, h.headline ?? {}) };
  const hurt = num(headline.badlyHurtHealthFrac);
  if (hurt < 0 || hurt > 1)
    fail('headline.badlyHurtHealthFrac', 'Home rules need headline.badlyHurtHealthFrac in [0, 1].');
  atLeast('headline.safeCapDays', headline.safeCapDays, 1);
  const s = { ...d.supplies, ...withDefaults({}, h.supplies ?? {}) };
  notNegative('supplies.shortDays', s.shortDays);
  if (num(s.lowDays) <= num(s.shortDays))
    fail('supplies.lowDays', 'Home rules need supplies.lowDays above supplies.shortDays.');
  notNegative('supplies.minBandages', s.minBandages);
  const full = num(s.storageAlmostFullFrac);
  if (full <= 0 || full > 1)
    fail(
      'supplies.storageAlmostFullFrac',
      'Home rules need supplies.storageAlmostFullFrac in (0, 1].',
    );
  for (const id of s.bandageItemIds ?? [])
    if (!items.has(id ?? ''))
      fail('supplies.bandageItemIds', `Home rules name unknown bandage item '${id ?? ''}'.`);
}

function itemGroups(
  groups: (ItemGroupDef | null)[] | null | undefined,
  section: BundleKey,
  what: string,
  items: Set<string>,
  build: boolean,
  err: Err,
) {
  const seen = new Set<string>();
  (groups ?? []).forEach((raw, i) => {
    const fail = (path: string, message: string) => err(section, i, path, message);
    if (raw == null) {
      fail('', `${what} list has an empty entry.`);
      return;
    }
    const g = withDefaults(itemGroupDefaults(), raw);
    const id = g.id ?? '';
    if (id === '' || !GROUP_ID.test(id))
      fail('id', `${what} '${id}' needs a lower-case id (letters, digits, _).`);
    else if (
      id === STORAGE_GROUP_OTHER ||
      (build && (BUILD_CATEGORY_RESERVED as readonly string[]).includes(id))
    )
      fail('id', `${what} '${id}' uses a reserved id.`);
    else if (seen.has(id)) fail('id', `${what} '${id}' is listed twice.`);
    else seen.add(id);
    if (!g.name) fail('name', `${what} '${id}' needs a name.`);
    const m = g.match ?? {};
    const empty =
      !m.survival &&
      !m.healAbove0 &&
      (m.tags?.length ?? 0) === 0 &&
      (m.categories?.length ?? 0) === 0 &&
      (m.itemIds?.length ?? 0) === 0;
    if (empty)
      fail(
        'match',
        `${what} '${id}' matches nothing (give it survival, healAbove0, tags, categories or itemIds).`,
      );
    for (const item of m.itemIds ?? [])
      if (!items.has(item ?? ''))
        fail('match.itemIds', `${what} '${id}' names unknown item '${item ?? ''}'.`);
  });
}

function autoHome(b: Bundle, err: Err) {
  if (b.bases === null) return; // the bases section's own check reports a missing block
  const r = withDefaults({ ...basesDefaults(), ...basesHbDefaults() }, b.bases ?? {});
  const fail = (path: string, message: string) => err('bases', undefined, path, message);
  const window = num(r.autoHomeWindowDays);
  const lead = num(r.autoHomeLeadDays);
  const minUse = num(r.autoHomeMinUseDays);
  if (window < 1) fail('autoHomeWindowDays', 'Bases autoHomeWindowDays must be at least 1.');
  if (lead < 0) fail('autoHomeLeadDays', 'Bases autoHomeLeadDays is negative.');
  if (minUse < 1) fail('autoHomeMinUseDays', 'Bases autoHomeMinUseDays must be at least 1.');
  if (minUse > window)
    fail('autoHomeMinUseDays', 'Bases need autoHomeMinUseDays at most autoHomeWindowDays.');
  if (lead >= window)
    fail('autoHomeLeadDays', 'Bases need autoHomeLeadDays below autoHomeWindowDays.');
  if (num(r.autoHomeFlagQuietHours) < 0)
    fail('autoHomeFlagQuietHours', 'Bases autoHomeFlagQuietHours is negative.');
}
